import { validationConstants } from "constants/formValidation";
import { orderTypeConstants, orderStatusConstants } from "constants/order";
import { ToastContext } from "context/ToastContext";
import { parseDate } from "helpers/date";
import useFormValidation from "hooks/useFormValidation";
import { Label } from "layout/typography/Label";
import {
  convertToUTC,
  validateInputDatePicker,
} from "pages/CreateOrder/utils/helpers";
import {
  handleChangeDateWithMask,
  handleChangePhoneWithMask,
} from "pages/CreateOrder/utils/mask";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { useContext, useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useNotifyMutation } from "store/queries/communications";
import { useUpdateOrderMutation } from "store/queries/order";
import { useUpdatePatientOverviewMutation } from "store/queries/patient";
import styled from "styled-components";
import { IDefaultServiceDetails, IOrder } from "types/Order/Order";

import { DetailField, SectionHeader } from "../SectionParts";
import { CustomTextDialog } from "./components/CustomTextDialog";

const StyledParagraph = styled.p<{
  outreachStopped: boolean | undefined | null;
}>`
  color: ${(props) =>
    props?.outreachStopped ? "var(--red-700)" : "var(--green-700)"} !important;
`;

interface IPatientEdit {
  email?: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  phoneNumber?: string;
  outOfPocketReason: string;
}

function PatientDetails({
  order: { patient, serviceDetails, orderType, id, status, outreachStopped },
  onUpdateOrder,
}: {
  order: IOrder;
  onUpdateOrder: () => void;
}) {
  const {
    control,
    trigger,
    getValues,
    setValue,
    setError,
    formState: { errors, dirtyFields, isValid },
    watch,
  } = useFormContext<IPatientEdit>();
  const [isLoading, setIsLoading] = useState(false);
  const dobField = watch("dateOfBirth");
  const [editing, setEdit] = useState(false);
  const [outreachButtonLoading, setOutreachButtonLoading] = useState(false);
  const [showCustomTextDialog, setShowCustomTextDialog] = useState(false);
  const [saveDisabled, setSaveDisabled] = useState(true);
  const [customText, setCustomText] = useState("");
  const [updatePatient, { isError: isUpdatePatientError }] =
    useUpdatePatientOverviewMutation();
  const [notify] = useNotifyMutation();
  const [updateOrder] = useUpdateOrderMutation();
  const {
    isValidPhoneNumber,
    handleEmailPhoneValidation,
    getFormErrorMessage,
    handleOnBlurField,
  } = useFormValidation();
  const { current: toastElement } = useContext(ToastContext);

  useEffect(() => {
    setValue("email", patient.email);
    setValue("firstName", patient.firstName);
    setValue("lastName", patient.lastName);
    setValue("dateOfBirth", parseDate(patient.dateOfBirth));
    setValue("phoneNumber", patient.phoneNumber);
  }, [patient, setValue]);

  const resetForm = () => {
    setValue("email", patient.email);
    setValue("firstName", patient.firstName);
    setValue("lastName", patient.lastName);
    setValue("dateOfBirth", parseDate(patient.dateOfBirth));
    setValue("phoneNumber", patient.phoneNumber);
  };

  useEffect(() => {
    const dobIsValid = validateInputDatePicker(dobField, "past") === true;
    const nameIsNotValid = !!getFormErrorMessage("firstName", errors);
    const lastNameIsNotValid = !!getFormErrorMessage("lastName", errors);
    const emailIsNotValid = !!getFormErrorMessage("email", errors);
    const phoneNumberIsNotValid = !!getFormErrorMessage("phoneNumber", errors);

    setSaveDisabled(
      !dobIsValid ||
        nameIsNotValid ||
        lastNameIsNotValid ||
        emailIsNotValid ||
        phoneNumberIsNotValid
    );
  }, [
    dobField,
    errors.email,
    errors.firstName,
    errors.lastName,
    errors.phoneNumber,
  ]);

  const mapPatientUpdateRequest = (values: IPatientEdit) => {
    let preferredContactMethod;
    if (values.email && values.phoneNumber) {
      preferredContactMethod = "Both";
    }
    if (!values.email && values.phoneNumber) {
      preferredContactMethod = "Phone";
    }
    if (values.email && !values.phoneNumber) {
      preferredContactMethod = "Email";
    }
    return {
      dateOfBirth: convertToUTC(values.dateOfBirth),
      email: values.email,
      firstName: values.firstName,
      lastName: values.lastName,
      phoneNumber: values.phoneNumber,
      preferredContactMethod,
    };
  };

  const submit = () => {
    setIsLoading(true);
    const values = mapPatientUpdateRequest(getValues());
    const EmailOrPhoneChanged =
      dirtyFields.email || dirtyFields.phoneNumber || false;

    updatePatient({ id: patient.id, data: values })
      .unwrap()
      .then(async () => {
        if (EmailOrPhoneChanged) {
          notify({ orderId: id })
            .unwrap()
            .then(async () => {
              await onUpdateOrder();
              toastElement?.show({
                summary: "Success",
                severity: "success",
                detail: "Patient edited successfully",
              });
            })
            .catch(async () => {
              await onUpdateOrder();
              toastElement?.show({
                severity: "error",
                detail: "Try again later.",
                summary: "Email failed to be sent. Please try again.",
              });
            });
        } else {
          await onUpdateOrder();
          toastElement?.show({
            summary: "Success",
            severity: "success",
            detail: "Patient edited successfully",
          });
        }
        setIsLoading(false);
        setEdit(false);
      })
      .catch(() => {
        if (isUpdatePatientError) {
          toastElement?.show({
            summary: "Error",
            severity: "error",
            detail: "Patient cannot be edited. Please try again.",
          });
        }
        setIsLoading(false);
        setEdit(false);
      });
  };

  const actionFooter = () => {
    return (
      <>
        <Button
          label="Save"
          icon="pi pi-check"
          onClick={submit}
          disabled={saveDisabled}
          loading={isLoading}
        />
        <Button
          label="Cancel"
          onClick={() => {
            resetForm();
            setEdit(false);
          }}
          icon="pi pi-times"
          className="p-button-outlined-light"
          style={{ marginLeft: "0.5em" }}
        />
      </>
    );
  };

  const enableEditing = () => {
    setEdit(true);
  };

  const handleResendNotification = () => {
    notify({ orderId: id, isResend: true })
      .unwrap()
      .then(() => {
        toastElement?.show({
          summary: "Success",
          severity: "success",
          detail: "Notification resent successfully",
        });
      })
      .catch(() => {
        toastElement?.show({
          summary: "Error",
          severity: "error",
          detail: "Failed to resend notification. Please try again.",
        });
      });
  };

  const handleChangeOutreach = () => {
    setOutreachButtonLoading(true);
    updateOrder({ id, outreachStopped: !outreachStopped })
      .unwrap()
      .then(async () => {
        await onUpdateOrder();
        setOutreachButtonLoading(false);
        toastElement?.show({
          summary: "Success!",
          severity: "success",
          detail: "Your estimate has been updated.",
        });
        setEdit(false);
      })
      .catch(() => {
        toastElement?.show({
          severity: "error",
          detail: "Try again later.",
          summary: "Something went wrong.",
        });
      });
  };

  const handleSendCustomText = () => {
    notify({ orderId: id, isResend: false, customText, onlyText: true })
      .unwrap()
      .then(() => {
        toastElement?.show({
          summary: "Success",
          severity: "success",
          detail: "Notification resent successfully",
        });
      })
      .catch(() => {
        toastElement?.show({
          summary: "Error",
          severity: "error",
          detail: "Failed to resend notification. Please try again.",
        });
      });
  };

  const outreachActions = () => {
    return (
      <>
        <StyledParagraph
          className="m-0 text-sm flex align-items-center gap-2"
          data-testid="outreachStopped"
          outreachStopped={outreachStopped}
        >
          <span
            className="inline-block border-circle"
            style={{
              width: "0.5rem",
              height: "0.5rem",
              background: "currentColor",
            }}
          />
          {outreachStopped ? "Outreach Stopped" : "Outreach Enabled"}
        </StyledParagraph>
        <div className="flex flex-wrap gap-2">
          <Button
            label={outreachStopped ? "Enable outreach" : "Stop outreach"}
            onClick={handleChangeOutreach}
            loading={outreachButtonLoading}
            className="h-fit p-button-outlined-light"
            icon={outreachStopped ? "pi pi-play" : "pi pi-stop-circle"}
          />
          {status === orderStatusConstants.SENT_TO_PATIENT && (
            <Button
              label="Resend Notification"
              onClick={handleResendNotification}
              className="h-fit p-button-outlined-light"
              icon="pi pi-bell"
            />
          )}
          {patient.preferredContactMethod !== "Email" && (
            <Button
              label="Send Custom Text"
              onClick={() => setShowCustomTextDialog(true)}
              className="h-fit p-button-outlined-light"
              icon="pi pi-send"
            />
          )}
        </div>
      </>
    );
  };

  const isDefaultOrder =
    orderType === orderTypeConstants.BUNDLED ||
    orderType === orderTypeConstants.BARIATRICS ||
    orderType === orderTypeConstants.ED;

  return (
    <>
      <CustomTextDialog
        showCustomTextDialog={showCustomTextDialog}
        setShowCustomTextDialog={setShowCustomTextDialog}
        customText={customText}
        setCustomText={setCustomText}
        onSendCustomText={handleSendCustomText}
      />
      <section
        className="px-3 pb-3 border-bottom-1"
        style={{
          borderColor: "var(--brand-gray-200)",
          marginLeft: "-1rem",
          marginRight: "-1rem",
        }}
      >
        <SectionHeader icon="pi pi-user" title="Patient">
          {!editing && (
            <Button
              icon="pi pi-pencil"
              className="ml-auto p-button-text p-button-plain p-button-rounded"
              aria-label="Edit patient"
              onClick={enableEditing}
            />
          )}
        </SectionHeader>

        {editing ? (
          <div className="formgrid grid w-100 mt-3">
            <div className="field flex flex-column col-12 md:col-3">
              <Label htmlFor="firstName" className="text-sm">
                First Name
              </Label>
              <Controller
                name="firstName"
                control={control}
                rules={{
                  required: validationConstants.PATIENT_INFO_FORM.FIRST_NAME,
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <>
                    <InputText
                      className="p-inputtext-sm"
                      disabled={!editing}
                      id="firstName"
                      aria-labelledby="firstName"
                      data-pii="patient-first-name"
                      value={value}
                      onChange={onChange}
                      onBlur={() => {
                        handleOnBlurField({
                          onBlur,
                          field: "firstName",
                          trigger,
                        });
                      }}
                    />
                    {getFormErrorMessage("firstName", errors)}
                  </>
                )}
              />
            </div>
            <div className="field flex flex-column col-12 md:col-3">
              <Label htmlFor="lastName" className="text-sm">
                Last Name
              </Label>
              <Controller
                name="lastName"
                control={control}
                rules={{
                  required: validationConstants.PATIENT_INFO_FORM.LAST_NAME,
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <>
                    <InputText
                      className="p-inputtext-sm"
                      disabled={!editing}
                      id="lastName"
                      aria-labelledby="lastName"
                      data-pii="patient-last-name"
                      value={value}
                      onChange={onChange}
                      onBlur={() => {
                        handleOnBlurField({
                          onBlur,
                          field: "lastName",
                          trigger,
                        });
                      }}
                    />
                    {getFormErrorMessage("lastName", errors)}
                  </>
                )}
              />
            </div>

            <div className="field flex flex-column col-12 md:col-6">
              <Label htmlFor="dob" className="text-sm">
                Date of Birth
              </Label>
              <Controller
                name="dateOfBirth"
                control={control}
                rules={{
                  validate: (date) => validateInputDatePicker(date, "past"),
                  required: validationConstants.PATIENT_INFO_FORM.DOB,
                }}
                render={({ field: { onChange, onBlur, value } }) => {
                  return (
                    <>
                      <InputText
                        className="p-inputtext-sm"
                        disabled={!editing}
                        value={value}
                        id="dateOfBirth"
                        placeholder="99/99/9999"
                        onBlur={() => {
                          handleOnBlurField({
                            onBlur,
                            field: "dateOfBirth",
                            trigger,
                          });
                        }}
                        onChange={(e) => handleChangeDateWithMask(e, onChange)}
                      />

                      {getFormErrorMessage("dateOfBirth", errors)}
                    </>
                  );
                }}
              />
            </div>

            <div className="field flex flex-column col-12 md:col-6">
              <Label htmlFor="phoneNumber" className="text-sm">
                Phone
              </Label>
              <Controller
                name="phoneNumber"
                control={control}
                rules={{
                  required: handleEmailPhoneValidation(
                    patient.preferredContactMethod,
                    "phone"
                  ),
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <>
                    <InputText
                      className="p-inputtext-sm"
                      disabled={!editing}
                      id="phoneNumber"
                      autoComplete="off"
                      aria-labelledby="phoneNumber"
                      value={value}
                      onChange={(e) => handleChangePhoneWithMask(e, onChange)}
                      onBlur={() => {
                        isValidPhoneNumber(
                          patient.preferredContactMethod,
                          value,
                          setError
                        );
                        return handleOnBlurField({
                          onBlur,
                          field: "phoneNumber",
                          trigger,
                        });
                      }}
                    />
                    {getFormErrorMessage("phoneNumber", errors)}
                  </>
                )}
              />
            </div>
            <div className="field flex flex-column col-12 md:col-6">
              <Label htmlFor="email" className="text-sm">
                Email
              </Label>
              <Controller
                name="email"
                control={control}
                rules={{
                  pattern: {
                    value: /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/g,
                    message: "Email is invalid.",
                  },
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <>
                    <InputText
                      className="p-inputtext-sm"
                      disabled={!editing}
                      id="email"
                      aria-labelledby="email"
                      value={value}
                      onChange={onChange}
                      onBlur={() => {
                        handleOnBlurField({ onBlur, field: "email", trigger });
                      }}
                    />
                    {getFormErrorMessage("email", errors)}
                  </>
                )}
              />
            </div>

            {isDefaultOrder && (
              <div className="field flex flex-column col-12 md:col-6">
                <Label htmlFor="outOfPocketReason" className="text-sm">
                  Out of pocket reason
                </Label>

                <InputText
                  className="p-inputtext-sm"
                  disabled
                  id="outOfPocketReason"
                  aria-labelledby="outOfPocketReason"
                  value={
                    (serviceDetails as IDefaultServiceDetails).outOfPocketReason
                  }
                />
              </div>
            )}
          </div>
        ) : (
          <div className="grid mt-3">
            <DetailField
              label="Full Name"
              value={`${patient.firstName} ${patient.lastName}`}
              pii="patient-name"
            />
            <DetailField
              label="Date of Birth"
              value={parseDate(patient.dateOfBirth)}
              pii="patient-name"
            />
            <DetailField label="Phone" value={patient.phoneNumber} />
            <DetailField label="Email" value={patient.email} />
            {isDefaultOrder && (
              <DetailField
                label="Out of pocket reason"
                value={
                  (serviceDetails as IDefaultServiceDetails).outOfPocketReason
                }
              />
            )}
          </div>
        )}

        <div
          className="flex flex-wrap align-items-center justify-content-between gap-2 mt-3 px-3 pt-3 border-top-1"
          style={{
            borderColor: "var(--brand-gray-200)",
            marginLeft: "-1rem",
            marginRight: "-1rem",
          }}
        >
          {editing ? (
            <div className="flex">{actionFooter()}</div>
          ) : (
            outreachActions()
          )}
        </div>
      </section>
    </>
  );
}

export default PatientDetails;
